import { useState } from 'react';
import type { Audience, LiveKind, RosterEntry } from '../../../shared/protocol.ts';
import { uid } from '../lib/emitter.ts';
import type { StudioEngine } from '../lib/studio.ts';
import { Avatar, Icon, roleLabel, useNow } from './common.tsx';

const KINDS: { kind: LiveKind; label: string }[] = [
  { kind: 'camera', label: 'Camera' },
  { kind: 'mic', label: 'Mic' },
  { kind: 'screen', label: 'Screen' },
];
const isStaffRole = (r: string) => r === 'host' || r === 'producer';

/**
 * Hosts' and producers' controls for the session: who waits in the lobby, the rooms (main studio and green
 * rooms, each recorded or not), moving people between them, and who in this room receives whose camera, mic and
 * screen.
 */
export function RoomsPanel({ engine }: { engine: StudioEngine }) {
  useNow(5000);
  const settings = engine.settings;
  if (!settings) {
    return (
      <div className="empty">
        <span className="spinner" /> Loading…
      </div>
    );
  }
  const waiting = engine.roster.filter((e) => e.roomId == null).sort((a, b) => (a.waitingSince ?? 0) - (b.waitingSince ?? 0));
  const byRoom = (roomId: string) => engine.roster.filter((e) => e.roomId === roomId);

  return (
    <>
      <div className="col" style={{ gap: 8 }}>
        <span className="eyebrow">Recordings</span>
        <KeepLocalChoice value={settings.keepLocal} onChange={(keepLocal) => engine.updateSettings((s) => ({ ...s, keepLocal }))} />
      </div>

      <div className="col" style={{ gap: 8 }}>
        <div className="section-title">
          <span className="eyebrow">Lobby{waiting.length ? ` · ${waiting.length} waiting` : ''}</span>
          <label className="switch-row" title="Guests wait until a host or producer lets them in. Hosts and producers always go straight in.">
            <input type="checkbox" checked={settings.lobby} onChange={(e) => engine.updateSettings((s) => ({ ...s, lobby: e.target.checked }))} />
            Guests wait to be let in
          </label>
        </div>
        {!waiting.length ? (
          <p className="panel-note">{settings.lobby ? 'Nobody is waiting.' : 'Guests come straight in.'}</p>
        ) : (
          waiting.map((w) => <WaitingRow key={w.id} engine={engine} entry={w} />)
        )}
      </div>

      <div className="col" style={{ gap: 10 }}>
        <div className="section-title">
          <span className="eyebrow">Rooms</span>
          <button
            className="sm"
            onClick={() =>
              engine.updateSettings((s) => ({
                ...s,
                rooms: [
                  ...s.rooms,
                  { id: `room-${uid().slice(0, 6)}`, name: `Green room ${s.rooms.length}`, record: false, layout: { preset: 'grid', order: [], focus: null, hidden: [] }, audience: {} },
                ],
              }))
            }
            disabled={settings.rooms.length >= 8}
          >
            <Icon name="plus" size={14} /> Green room
          </button>
        </div>
        {settings.rooms.map((r) => (
          <div key={r.id} className={`room-card ${engine.room?.id === r.id ? 'mine' : ''}`}>
            <div className="row" style={{ gap: 8 }}>
              <RoomName key={`${r.id}:${r.name}`} engine={engine} roomId={r.id} name={r.name} />
              <label className="switch-row small" title="People in this room are recorded when a take runs">
                <input type="checkbox" checked={r.record} onChange={(e) => engine.updateRoom(r.id, (x) => (x.record = e.target.checked))} />
                Recorded
              </label>
              {r.id !== 'stage' && (
                <button
                  className="ghost sm icon"
                  title="Remove room (anyone in it goes to the main studio)"
                  aria-label={`Remove ${r.name}`}
                  onClick={() => engine.updateSettings((s) => ({ ...s, rooms: s.rooms.filter((x) => x.id !== r.id) }))}
                >
                  <Icon name="close" size={13} />
                </button>
              )}
            </div>
            {byRoom(r.id).length === 0 ? (
              <span className="faint small">Empty</span>
            ) : (
              byRoom(r.id).map((p) => (
                <div key={p.id} className="person-row">
                  <Avatar name={p.name} size={22} />
                  <span className="grow ellipsis">
                    {p.name}
                    {p.id === engine.selfId ? ' (you)' : ''} <span className="faint small">· {roleLabel(p.role)}</span>
                  </span>
                  {settings.rooms.length > 1 && (
                    <select
                      className="sm"
                      value={r.id}
                      aria-label={`Room for ${p.name}`}
                      title="Move to another room"
                      onChange={(e) => engine.moveTo(p.id, e.target.value)}
                    >
                      {settings.rooms.map((x) => (
                        <option key={x.id} value={x.id}>
                          {x.id === r.id ? `In ${x.name}` : `Move to ${x.name}`}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              ))
            )}
          </div>
        ))}
      </div>

      {engine.room && <WhoSeesWhom engine={engine} />}
    </>
  );
}

function WaitingRow({ engine, entry }: { engine: StudioEngine; entry: RosterEntry }) {
  const [roomId, setRoomId] = useState('stage');
  const rooms = engine.settings?.rooms ?? [];
  const mins = entry.waitingSince ? Math.max(0, Math.round((Date.now() - entry.waitingSince) / 60000)) : 0;
  return (
    <div className="waiting-row">
      <Avatar name={entry.name} size={26} />
      <div className="col grow" style={{ gap: 0, minWidth: 0 }}>
        <strong className="ellipsis">{entry.name}</strong>
        <span className="faint small">Waiting {mins ? `${mins} min` : 'just now'}</span>
      </div>
      {rooms.length > 1 && (
        <select className="sm" value={roomId} onChange={(e) => setRoomId(e.target.value)} aria-label={`Room for ${entry.name}`}>
          {rooms.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </select>
      )}
      <button className="primary sm" onClick={() => engine.admit(entry.id, roomId)}>
        Let in
      </button>
      <button className="ghost sm" onClick={() => engine.turnAway(entry.id)} title="They’ll be told the host didn’t let them in">
        Turn away
      </button>
    </div>
  );
}

function RoomName({ engine, roomId, name }: { engine: StudioEngine; roomId: string; name: string }) {
  const [value, setValue] = useState(name);
  const commit = () => value.trim() && value.trim() !== name && engine.updateRoom(roomId, (r) => (r.name = value.trim()));
  return (
    <input
      className="name room-name grow"
      value={value}
      aria-label="Room name"
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
    />
  );
}

/** In this room: for each person, who receives their camera, mic and screen. Hosts and producers always do. */
function WhoSeesWhom({ engine }: { engine: StudioEngine }) {
  const roomId = engine.room!.id;
  const config = engine.settings?.rooms.find((r) => r.id === roomId);
  const people = engine.roster.filter((e) => e.roomId === roomId);
  const guests = people.filter((p) => !isStaffRole(p.role));
  const [open, setOpen] = useState<string | null>(null);
  if (!config || people.length < 2) return null;
  return (
    <div className="col" style={{ gap: 8 }}>
      <span className="eyebrow">Who sees and hears whom · {config.name}</span>
      <p className="panel-note">Everyone in the room gets everyone by default. Hosts and producers always get everything. Changes apply at once.</p>
      {people.map((p) => {
        const rules = config.audience[p.participantId] ?? {};
        const custom = Object.values(rules).some((a) => a !== 'everyone');
        return (
          <div key={p.id} className="audience-card">
            <button className="audience-head linklike" onClick={() => setOpen(open === p.id ? null : p.id)} aria-expanded={open === p.id}>
              <Avatar name={p.name} size={20} />
              <span className="grow ellipsis">{p.name}</span>
              <span className={`pill ${custom ? 'accent' : ''}`}>{custom ? 'Restricted' : 'Everyone'}</span>
            </button>
            {open === p.id && (
              <div className="col" style={{ gap: 8 }}>
                {KINDS.map(({ kind, label }) => (
                  <AudienceRow
                    key={kind}
                    label={label}
                    value={rules[kind] ?? 'everyone'}
                    candidates={guests.filter((g) => g.participantId !== p.participantId)}
                    onChange={(a) => engine.setAudience(p.participantId, kind, a)}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function AudienceRow({ label, value, candidates, onChange }: { label: string; value: Audience; candidates: RosterEntry[]; onChange: (a: Audience) => void }) {
  const mode = typeof value === 'object' ? 'only' : value;
  const only = typeof value === 'object' ? value.only : [];
  return (
    <div className="audience-row">
      <span className="small" style={{ width: 56 }}>
        {label}
      </span>
      <select
        className="sm grow"
        value={mode}
        aria-label={`${label}: who receives it`}
        onChange={(e) => onChange(e.target.value === 'only' ? { only: [] } : (e.target.value as 'everyone' | 'staff'))}
      >
        <option value="everyone">Everyone in the room</option>
        <option value="staff">Hosts and producers only</option>
        <option value="only">Hosts, producers and…</option>
      </select>
      {mode === 'only' && (
        <div className="chips" style={{ width: '100%' }}>
          {candidates.length === 0 && <span className="faint small">No other guests here</span>}
          {candidates.map((c) => {
            const on = only.includes(c.participantId);
            return (
              <button
                key={c.participantId}
                className={`chip-toggle ${on ? 'on' : ''}`}
                aria-pressed={on}
                onClick={() => onChange({ only: on ? only.filter((x) => x !== c.participantId) : [...only, c.participantId] })}
              >
                {c.name}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Where a studio's recordings go: uploaded to the studio, or kept on each person's computer. */
export function KeepLocalChoice({ value, onChange }: { value: boolean; onChange: (keepLocal: boolean) => void }) {
  return (
    <div className="col" style={{ gap: 6 }}>
      <select className="sm" value={value ? 'local' : 'upload'} onChange={(e) => onChange(e.target.value === 'local')} aria-label="Where recordings go">
        <option value="upload">Upload to the studio</option>
        <option value="local">Keep on each person’s computer</option>
      </select>
      <p className="panel-note">
        {value
          ? 'Files stay where they were recorded; only their timings come to the studio, so the timeline still lines everything up. Anyone can upload a take later, or copy it to a folder, from “On this computer”. Applies from the next take.'
          : 'Everyone’s files upload in the background to the Files page, for downloads, the zip and transcripts. Applies from the next take.'}
      </p>
    </div>
  );
}
